import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useI18n } from '../../context/I18nContext';
import { Project, ProjectStatus, DeliverableType, Lead } from '../../types';
import BaseFormModal from '../ui/BaseFormModal';
import Input from '../ui/Input';
import Select from '../ui/Select';
import DatePicker from '../ui/DatePicker';
import LeadSearch from '../messages/LeadSearch';
import TextArea from '../ui/TextArea';
import { type DurationUnit, daysToDisplay, toDays, addDurationToLocalDate } from '../../utils/rightsDuration';
import type { RequestCreateLeadArgs } from '../../hooks/useCreateLeadFromSearch';
import { commitFormSave } from '../../utils/commitFormSave';

interface ProjectFormData {
  name: string;
  leadId: string;
  type: DeliverableType;
  status: ProjectStatus;
  brief: string;
  amount: number;
  currency: string;
  deadline: string;
  deadlineDate?: Date | null;
  deliverables: string;
  platforms: string;
  duration: number;
  durationUnit: DurationUnit;
  territory: string;
  exclusivity: boolean;
}

const formatLocalDateInput = (date: Date): string => {
  const pad2 = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => void;
  project?: Project | null;
  leads: Lead[];
  searchLeadsFn?: (term: string) => Promise<Lead[]>;
  preselectedLead?: Lead | null;
  title?: string;
  /** Prefill project name when creating from ProjectSearch. */
  defaultName?: string;
  /** Prefill status when creating from a Kanban column. */
  defaultStatus?: ProjectStatus;
  /** Opens LeadModal from LeadSearch; onCreated selects the new lead. */
  onCreateLead?: (args: RequestCreateLeadArgs) => void;
  /** Await onSave then close (create-from-search). */
  closeAfterSave?: boolean;
}

const ProjectModal: React.FC<ProjectModalProps> = ({
  isOpen,
  onClose,
  onSave,
  project,
  leads,
  searchLeadsFn,
  preselectedLead,
  title,
  defaultName = '',
  defaultStatus = 'BRIEF_RECEIVED',
  onCreateLead,
  closeAfterSave = false,
}) => {
  const { t } = useI18n();
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isAssignmentRightsExpanded, setIsAssignmentRightsExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  /** Avoid create-mode re-init when `leads` updates after inline create. */
  const createInitializedRef = useRef(false);
  const assignmentRightsSectionRef = useRef<HTMLDivElement>(null);
  /** Skip auto-scroll when rights are expanded from initial project hydrate. */
  const skipAssignmentRightsScrollRef = useRef(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
    watch,
  } = useForm<ProjectFormData>({ defaultValues: { durationUnit: 'months' } });

  // When user expands rights section, bring it into view inside the modal scroll area
  useEffect(() => {
    if (!isOpen || !isAssignmentRightsExpanded) return;
    if (skipAssignmentRightsScrollRef.current) {
      skipAssignmentRightsScrollRef.current = false;
      return;
    }
    const el = assignmentRightsSectionRef.current;
    if (!el) return;
    const frame = requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen, isAssignmentRightsExpanded]);

  useEffect(() => {
    if (!isOpen) {
      createInitializedRef.current = false;
      return;
    }

    if (project) {
      // Edit mode — keep in sync when leads list resolves
      const lead = leads.find(l => l.id === project.leadId);
      setSelectedLead(lead || null);
      setValue('name', project.name);
      setValue('leadId', project.leadId);
      setValue('type', project.type);
      setValue('status', project.status);
      setValue('brief', project.brief || '');
      setValue('amount', project.amount);
      setValue('currency', project.currency);
      setValue('deadline', project.deadline ? formatLocalDateInput(new Date(project.deadline)) : '');
      setValue('deadlineDate', project.deadline ? new Date(project.deadline) : null);
      setValue('deliverables', project.deliverables.join(', '));
      skipAssignmentRightsScrollRef.current = true;
      setIsAssignmentRightsExpanded(!!project.rights);
      setValue('platforms', project.rights?.platforms.join(', ') || '');
      if (project.rights?.duration) {
        const { value, unit } = daysToDisplay(project.rights.duration);
        setValue('duration', value);
        setValue('durationUnit', unit);
      } else {
        setValue('duration', 12);
        setValue('durationUnit', 'months');
      }
      setValue('territory', project.rights?.territory || 'France');
      setValue('exclusivity', project.rights?.exclusivity || false);
      return;
    }

    // Create mode: init once per open (do not wipe lead selection after inline create)
    if (createInitializedRef.current) {
      // Deep link: lead fetched after modal opened — apply when it arrives
      if (preselectedLead && !getValues('leadId')) {
        setSelectedLead(preselectedLead);
        setValue('leadId', preselectedLead.id, { shouldValidate: true });
        const currentName = getValues('name');
        if (!currentName?.trim()) {
          setValue('name', preselectedLead.name);
        }
      }
      return;
    }
    createInitializedRef.current = true;

    const leadToSelect = preselectedLead || (leads.length === 1 ? leads[0] : null);
    setSelectedLead(leadToSelect);
    setIsAssignmentRightsExpanded(false);
    const prefillName = defaultName.trim() || leadToSelect?.name || '';
    reset({
      name: prefillName,
      leadId: leadToSelect?.id || '',
      type: 'short_video',
      status: defaultStatus,
      brief: '',
      amount: 0,
      currency: 'EUR',
      deadline: '',
      deadlineDate: null,
      deliverables: '',
      platforms: '',
      duration: 12,
      durationUnit: 'months',
      territory: 'France',
      exclusivity: false,
    });
  }, [isOpen, project, leads, preselectedLead, defaultName, defaultStatus, setValue, reset, getValues]);

  const handleLeadSelect = (lead: Lead | null) => {
    setSelectedLead(lead);
    if (lead) {
      setValue('leadId', lead.id, { shouldValidate: true, shouldDirty: true });
      // Pre-fill name with lead name if name field is empty
      const currentName = getValues('name');
      if (!currentName || currentName.trim() === '') {
        setValue('name', lead.name);
      }
    } else {
      setValue('leadId', '', { shouldValidate: true, shouldDirty: true });
    }
  };

  const onSubmit = async (data: ProjectFormData) => {
    const projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt'> = {
      name: data.name.trim(),
      leadId: data.leadId,
      type: data.type,
      status: data.status || 'BRIEF_RECEIVED', // Ensure we always have a valid status
      brief: data.brief || undefined,
      amount: Number(data.amount),
      currency: data.currency,
      deadline: data.deadline ? new Date(data.deadline) : undefined,
      deliverables: data.deliverables.split(',').map(d => d.trim()).filter(d => d),
      files: project?.files || [],
      rights: isAssignmentRightsExpanded ? {
        id: project?.rights?.id || '',
        projectId: project?.id || '',
        platforms: data.platforms.split(',').map(p => p.trim()).filter(p => p),
        duration: toDays(Number(data.duration), data.durationUnit),
        territory: data.territory,
        exclusivity: data.exclusivity,
        startDate: (() => {
          const existingStart = project?.rights?.startDate;
          if (!existingStart) return null;
          return existingStart instanceof Date ? existingStart : new Date(existingStart);
        })(),
        endDate: (() => {
          const existingStart = project?.rights?.startDate;
          if (!existingStart) return null;
          const start = existingStart instanceof Date ? existingStart : new Date(existingStart);
          return addDurationToLocalDate(start, Number(data.duration), data.durationUnit);
        })(),
        checkedAt: null,
        extensions: project?.rights?.extensions || [],
      } : null,
    };

    await commitFormSave({
      closeAfterSave,
      onSave: () => onSave(projectData),
      onClose,
      setSaving: setIsSaving,
    });
  };

  const typeOptions = [
    { value: 'short_video', label: '🎬 Vidéo courte' },
    { value: 'photo', label: '📸 Photo' },
    { value: 'voice_over', label: '🎙️ Voice Over' },
    { value: 'story', label: '📱 Story' },
    { value: 'post', label: '🖼️ Post / Carrousel' },
    { value: 'long_video', label: '🎥 Vidéo longue' },
    { value: 'other', label: '📄 Autre' },
  ];

  const statusOptions = [
    { value: 'BRIEF_RECEIVED', label: t('projects.statusBriefReceived') },
    { value: 'IN_PRODUCTION', label: t('projects.statusInProduction') },
    { value: 'V1_SENT', label: t('projects.statusFirstVersion') },
    { value: 'CHANGES_REQUESTED', label: t('projects.statusCorrections') },
    { value: 'DELIVERED', label: t('projects.statusDelivered') },
    { value: 'ARCHIVED', label: t('projects.statusArchived') },
  ];

  const currencyOptions = [
    { value: 'EUR', label: '€ Euro' },
    { value: 'USD', label: '$ Dollar' },
    { value: 'GBP', label: '£ Livre' },
  ];


  return (
    <BaseFormModal
      isOpen={isOpen}
      onClose={onClose}
      resourceKey="projects"
      size="xl"
      isLoading={isSaving}
      mode={project ? 'update' : 'create'}
      onValidate={handleSubmit(onSubmit)}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {/* Register non-input fields for validation */}
        <input type="hidden" {...register('leadId', { required: t('projects.leadRequired') as any })} />
        <input type="hidden" {...register('type', { required: t('projects.typeRequired') as any })} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t('projects.name')}
            {...register('name', { required: t('projects.nameRequired') as any })}
            error={errors.name?.message}
            placeholder={t('projects.namePlaceholder')}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('projects.lead')}
            </label>
            <LeadSearch
              leads={leads}
              selectedLead={selectedLead}
              onSelectLead={handleLeadSelect}
              placeholder={t('projects.searchLead')}
              error={errors.leadId?.message}
              searchFn={searchLeadsFn}
              onCreateLead={onCreateLead}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <Select
            label={t('projects.deliverableType')}
            value={watch('type') || ''}
            onChange={(value) => setValue('type', value as DeliverableType, { shouldValidate: true, shouldDirty: true })}
            options={typeOptions}
            error={errors.type?.message}
          />

          <Select
            label={t('projects.status')}
            value={watch('status') || ''}
            onChange={(value) => setValue('status', value as ProjectStatus)}
            options={statusOptions}
          />

          <DatePicker
            label={t('projects.deadline')}
            value={watch('deadlineDate') || (watch('deadline') && watch('deadline') !== '' ? new Date(watch('deadline')!) : null)}
            onChange={(date) => {
              setValue('deadlineDate', date);
              setValue('deadline', date ? formatLocalDateInput(date) : '');
            }}
            error={errors.deadline?.message}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label={t('projects.amount')}
            type="number"
            step="0.01"
            {...register('amount', {
              required: t('projects.amountRequired') as any,
              min: { value: 0, message: t('projects.amountPositive') as any },
            })}
            error={errors.amount?.message}
            placeholder={t('projects.amountPlaceholder')}
          />

          <Select
            label={t('projects.currency')}
            value={watch('currency') || ''}
            onChange={(value) => setValue('currency', value)}
            options={currencyOptions}
          />
        </div>

        <Input
          label={t('projects.deliverables')}
          {...register('deliverables')}
          placeholder={t('projects.deliverablesPlaceholder')}
        />

        <TextArea
          label={t('projects.description')}
          {...register('brief')}
          rows={3}
          placeholder={t('projects.descriptionPlaceholder')}
        />

        <div ref={assignmentRightsSectionRef} className="border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={() => setIsAssignmentRightsExpanded(!isAssignmentRightsExpanded)}
            className="flex items-center justify-between w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-0 rounded-lg"
          >
            <span className="text-sm font-medium text-gray-900">{t('projects.assignmentRights')}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">
                {isAssignmentRightsExpanded ? t('projects.hasAssignmentRights') : t('projects.noAssignmentRights')}
              </span>
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                  isAssignmentRightsExpanded ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {isAssignmentRightsExpanded && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label={t('projects.platforms')}
                  {...register('platforms')}
                  placeholder={t('projects.platformsPlaceholder')}
                />

                <Input
                  label={t('projects.territory')}
                  {...register('territory')}
                  placeholder={t('projects.territoryPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="grid grid-cols-2 gap-2 items-end">
                  <div className="min-w-0">
                    <Input
                      label={t('projects.durationLabel')}
                      type="number"
                      {...register('duration', {
                        min: { value: 1, message: t('projects.durationMin') as any },
                      })}
                      error={errors.duration?.message}
                      placeholder={t('projects.durationPlaceholder')}
                    />
                  </div>
                  <div className="min-w-0">
                    <Select
                      value={watch('durationUnit') ?? 'months'}
                      onChange={(v) => setValue('durationUnit', v as DurationUnit)}
                      options={[
                        { value: 'days', label: t('projects.durationUnitDays') },
                        { value: 'weeks', label: t('projects.durationUnitWeeks') },
                        { value: 'months', label: t('projects.durationUnitMonths') },
                      ]}
                      className="min-w-0"
                    />
                  </div>
                </div>

                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('projects.exclusiveRights')}
                  </label>
                  <div className="flex items-center justify-start h-10">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        {...register('exclusivity')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 peer-focus:ring-offset-0 peer-checked:bg-primary-500 transition-colors"></div>
                      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-5"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </form>
    </BaseFormModal>
  );
};

export default ProjectModal;
